import * as api from "../lib/api";
import { isNotNullOrEmpty } from "./utilities";
import { findBookByTitle } from "./search";
import {
  AMAZON_AUTHOR_URL,
  FACEBOOK_URL,
  X_URL,
  YOUTUBE_URL,
  GOODREADS_URL,
  CHIRPBOOKS_URL,
} from "./links";

const GREETING_PATTERN = /^(hi|hello|hey|howdy|greetings)\b/;

// Process user message and generate response
export const processMessage = (userMessage) => {
  // Convert to lowercase for easier matching
  const query = userMessage.toLowerCase();

  if (GREETING_PATTERN.test(query.trim())) {
    return "Hello! I'm the Book Assistant. Ask me about any of Chris's books, formats, pricing, or where to buy them!";
  }

  // Check for general questions
  if (
    query.includes("genre") ||
    query.includes("write") ||
    query.includes("type")
  ) {
    return `Chris primarily writes fiction books, with some non-fiction thrown in. He also blogs at [BeingRefactored.com](https://beingrefactored.com).`;
  }

  if (
    query.includes("next") ||
    query.includes("coming") ||
    query.includes("release")
  ) {
    const nextBook = api.getNextUpcomingBook();
    if (isNotNullOrEmpty(nextBook)) {
      return `That's a secret! Chris's upcoming book is [${nextBook.title}](/book/${nextBook.id}) and will be released by ${nextBook.pubDate}. Check the [Featured Books](/#featured) section for a pre-order too!`;
    } else {
      const latestBook = api.getLatestBook();
      return `Doh! It looks like Chris doesn't have any upcoming books at this time. His latest book is  [${latestBook.title}](/book/${latestBook.id}) and was published on ${latestBook.pubDate}.`;
    }
  }

  if (
    query.includes("latest") ||
    query.includes("newest") ||
    query.includes("recent")
  ) {
    const latestBook = api.getLatestBook();
    return `Chris's latest book is [${latestBook.title}](/book/${latestBook.id}) and was published on ${latestBook.pubDate}.`;
  }

  if (
    query.includes("facebook") ||
    query.includes("goodreads") ||
    query.includes("instagram") ||
    query.includes("media") ||
    query.includes("social")
  ) {
    return `Yes! Chris is on [Facebook](${FACEBOOK_URL}), [X/Twitter](${X_URL}), [YouTube](${YOUTUBE_URL}), and [Goodreads](${GOODREADS_URL}).`;
  }

  if (
    query.includes("barnes") ||
    query.includes("borders") ||
    query.includes("amazon") ||
    query.includes("purchase") ||
    query.includes("sold") ||
    query.includes("sale") ||
    query.includes("order") ||
    query.includes("buy")
  ) {
    return `All books are available on [Amazon.com](${AMAZON_AUTHOR_URL}) or [ChirpBooks](${CHIRPBOOKS_URL}) (audio only)!`;
  }

  if (
    query.includes("format") ||
    query.includes("audio") ||
    query.includes("paperback") ||
    query.includes("ebook") ||
    query.includes("kindle")
  ) {
    return "All books are available in eBook format for the Kindle, and most are available in audio and paperback versions.";
  }

  if (query.includes("review")) {
    return `Reviews are always welcome! Please go to where you purchased the book and leave a review there (typically on Amazon). Thanks!`;
  }

  if (
    query.includes("question") ||
    query.includes("comment") ||
    query.includes("contact") ||
    query.includes("feedback")
  ) {
    return `Ok, you can ask the Chat or reach out to Chris via email or social-media. Thanks!`;
  }

  if (
    query.includes("price") ||
    query.includes("cost") ||
    query.includes("how much")
  ) {
    return `Prices vary by title and format (eBook, paperback, or audio). Check the listing on [Amazon.com](${AMAZON_AUTHOR_URL}) for current pricing.`;
  }

  if (
    query.includes("who is chris") ||
    query.includes("about chris") ||
    query.includes("about the author") ||
    query.includes("who is the author")
  ) {
    return `Chris Hambleton (writing as C.W. Hambleton) is the author of these books. You can learn more in the [About](/#about) section of the site.`;
  }

  // Check for a book this message is (fuzzily) about, by title
  const matchedBook = findBookByTitle(query, api.getVisibleBooks());
  if (matchedBook) {
    return `[${matchedBook.title}](/book/${matchedBook.id}) is a *${
      matchedBook.genres[0]
    }* book published in ${
      matchedBook.pubDate
    }. The description is: "${matchedBook.description.substring(
      0,
      100
    )}..." [read more](/book/${matchedBook.id})`;
  }

  // check search results
  const searchResults = api.searchItems(query, api.getVisibleBooks());
  if (searchResults.length > 0) {
    return `I found ${
      searchResults.length
    } item(s) that match your search: ${searchResults
      .map((x) => `[${x.title}](/book/${x.id})`)
      .join(
        ", "
      )}. You can also see the [Search Results](/search/${encodeURIComponent(
      query
    )}) page for more details.`;
  }

  // Default response for unknown queries
  return "I'm not sure about that. You can ask me about specific books, the latest releases, or where to buy the books. Otherwise try [Grok](https://x.ai/), [Claude](https://claude.ai/), or [ChatGPT](https://chatgpt.com/). We AI's know *everything*! :)";
};
